import { Injectable } from '@angular/core';
import { COLLECTION_NAMES } from '@libs/constants/collections.const';
import { UserStudent } from '@libs/models/user.models';
import { AuthService } from '@libs/services/firebase/auth.service';
import { FirestoreService, Params } from '@libs/services/firebase/firestore.service';
import { toDateObject } from '@libs/utils/date.utils';
import { BehaviorSubject } from 'rxjs';
import { UserRole } from '../models/user.model';
import { RegistersType } from '@libs/models/registers.models';
import { SUB_COLLECTION_NAME } from '@libs/constants/subCollections.const';
import { AvatarService } from './avatar.service';
import { Sprite } from '@libs/models/avatar.model';
import { TenantService } from '@libs/services/tenant.service';

@Injectable({
  providedIn: 'root'
})
export class UserService extends FirestoreService {

  private static isInit = false;
  private static unsub = () => { };

  public currentUser: BehaviorSubject<UserStudent | null> = new BehaviorSubject<UserStudent | null>(null);

  constructor(
    private authService: AuthService,
    private avatarService: AvatarService,
    private tenantService: TenantService
  ) {
    super();

    this.setCollectionRef(COLLECTION_NAMES.USERS);
  }

  setupUser() {
    if (UserService.isInit) return;
    UserService.isInit = true;

    this.authService.isAuthenticated.subscribe({
      next: isAuthenticated => {
        if (isAuthenticated) {
          this.setUser();

        } else {
          this.currentUser.next(null);
          UserService.unsub();
        }
      }
    });
  }

  private async setUser() {
    const current = this.authService.currentUser;
    if (!current || !current.uid) return;

    UserService.unsub();
    const docRef = this.getDocRef(current.uid);
    UserService.unsub = this.onSnapshot(docRef, (doc: any) => {
      const user = doc.data();

      if (user) {
        this.currentUser.next(user);
      }
    });
  }

  async getCurrentUser(): Promise<UserStudent | null> {
    const current = this.authService.currentUser;
    if (!current || !current.uid) {
      return null;
    }
    const user: UserStudent = await this.get(current.uid);

    return user;
  }

  async getUserbyId(uid: string) {
    const user: UserStudent = await this.get(uid);

    return user;
  }

  register(user: UserStudent) {
    const normalized = this.normalize({
      ...user,
      createdAt: new Date(),
      updatedAt: new Date(),
      role: UserRole.STUDENT,
      active: true,
    });

    return this.setDoc(normalized, normalized.uid);
  }

  public async getUsersForChat(tenantId?: string | null) {
    const param: Params = {
      where: [
        { key: 'role', condition: '!=', value: UserRole.STUDENT },
        { key: 'active', condition: '==', value: true },
      ]
    };

    if (!!tenantId) {
      param.where?.push(
        { key: 'tenant.id', condition: '==', value: tenantId }
      )
    }

    return this.load(param, true);
  }

  public async getUserByUniversity(idUniversity: string, role: string, active: boolean = true) {
    const users = await this.load({
      where: [
        { key: 'university.id', condition: '==', value: idUniversity },
        { key: 'role', condition: '==', value: role },
        { key: 'active', condition: '==', value: true }
      ]
    }, true);

    return users;
  }

  public async getMyPath(uid: string, myPath: RegistersType) {
    this.setSubCollection(COLLECTION_NAMES.USERS, SUB_COLLECTION_NAME.MYPATH, uid);

    const param: Params = {
      where: [
        { key: 'id', condition: '==', value: myPath }
      ]
    };

    try {
      const data = await this.load(param);

      return data[0];
    } finally {
      this.setCollectionRef(COLLECTION_NAMES.USERS);
    }
  }

  override async update(user: UserStudent) {
    const normalized = this.normalize({
      ...user,
      updatedAt: new Date(),
    });

    return this.set(normalized, normalized.uid);
  }

  public async getAvatarSprites(): Promise<Sprite[]> {
    const uid = this.authService.currentUser?.uid;
    if (!uid) return [];

    this.setSubCollection(COLLECTION_NAMES.USERS, SUB_COLLECTION_NAME.AVATAR, uid);

    try {
      const docs = await this.load({}, true);
      const sprites: Sprite[] = [];

      docs.forEach((data) => {
        const value = data['sprite'] ?? data;

        sprites.push(...(Array.isArray(value) ? value : [value]));
      });

      return sprites
        .map((sprite) => ({ ...sprite, items: Array.isArray(sprite?.items) ? sprite.items.filter(Boolean) : [] }))
        .filter((sprite) => !!sprite.items.length);
    } finally {
      this.setCollectionRef(COLLECTION_NAMES.USERS);
    }
  }

  public async getAvatarItens(): Promise<Sprite[]> {
    const user: UserStudent | null = await this.getCurrentUser() ?? null;
    const freshmanIsValid: boolean = this.getFreshmanMissions(user);
    const sophomoreIsValid: boolean = this.getShophomoreMissions(user);
    const juniorIsValid: boolean = this.getJuniorMissions(user);

    const sprite = this.avatarService.applyItemAvailability(
      this.avatarService.loadAll(),
      [
        { name: 'Camisa time', enabled: freshmanIsValid },
        { name: 'Mochila', colorName: 'Azul', enabled: freshmanIsValid },
        { name: 'Bola', enabled: sophomoreIsValid },
        { name: 'Shorts', enabled: sophomoreIsValid },
        { name: 'Chuteira', enabled: juniorIsValid },
        { name: 'Acessorio', enabled: juniorIsValid },
        { name: 'Mochila', colorName: 'Vermelho', enabled: !!user?.missionsSeniorNegotiation  },
        { name: 'Chapeu', enabled: !!user?.missionsSeniorI20 },
        { name: 'Camisa 2SV', enabled: false },
      ]
    );

    return sprite;
  }

  private getFreshmanMissions(user: any): boolean {
    const allCompleted =
      user?.missionsFreshManEducationalBase &&
      user?.missionsFreshManOneTrack &&
      user?.missionsFreshManSportsBase &&
      user?.missionsFreshManTrack;

    if (allCompleted) return allCompleted;

    return false;
  }

  private getShophomoreMissions(user: any): boolean {
    const allCompleted =
      user?.missionsSophomorePreferences &&
      user?.missionsSophomoreAdditionalInformation &&
      user?.missionsSophomoreTrack &&
      user?.missionSophomoreEvent;

    if (allCompleted) return allCompleted;

    return false;
  }

  private getJuniorMissions(user: any): boolean {
    const allCompleted =
      user?.missionJuniorEvent &&
      user?.missionsJuniorDocument &&
      user?.missionsJuniorTrack;

    if (allCompleted) return allCompleted;

    return false;
  }

  private normalize(user: UserStudent): UserStudent {
    if (user?.name) {
      user.name = user.name.trim();
    }

    if (user?.lastName) {
      user.lastName = user.lastName.trim();
    }

    if (user?.email) {
      user.email = user.email.trim().toLowerCase();
    }

    if (user?.birthDate) {
      user.birthDate = toDateObject(user.birthDate) ?? undefined;
    }

    return user;
  }

}
